import Box from "@mui/material/Box";
import { DataGrid, gridClasses, useGridApiRef } from "@mui/x-data-grid";
import { useCallback, useContext, useEffect, useRef } from "react";
import {
  PP_BRAND_COLORS_PRIMARY,
  PP_BRAND_COLORS_SECONDARY,
} from "../../../../../config/constants";
import currencyFormat from "../../../../../utils/currency-format";
import useUserStore from "../../../store/user/userStore";
import useColumns from "./useColumns";
import {
  DebtSupplierCoreItem,
  DebtSupplierCoreStatus,
} from "../../../../../types/debtType";
import { PortalConfigContext } from "../../../../../providers/PortalConfigProvider";

export default function DataTable() {
  const apiRef = useGridApiRef();
  const didMount = useRef(false);
  const { debts, checkDebts, updateDebt } = useUserStore((state) => ({
    debts: state.debts,
    checkDebts: state.checkDebts,
    updateDebt: state.updateDebt,
  }));

  const portalConfig = useContext(PortalConfigContext);

  const isRowSelectable = useCallback(
    (installment: number, key: string) => {
      if (portalConfig?.unsortedPayments || installment === 0) {
        return true;
      }

      const prevInstallments = debts.filter(
        (e) => e.installment < installment && e.details === key
      );

      if (!prevInstallments?.length) {
        return true;
      }

      for (const prevInstallment of prevInstallments) {
        if (
          prevInstallment?.status === DebtSupplierCoreStatus.Pending &&
          !prevInstallment?.selected
        ) {
          return false;
        } else if (
          prevInstallment?.status === DebtSupplierCoreStatus.Pending &&
          prevInstallment?.selected
        ) {
          if (
            prevInstallment.finalAmount.value < prevInstallment.amount.value
          ) {
            return false;
          }
        }
      }

      return true;
    },
    [debts, portalConfig?.unsortedPayments]
  );

  useEffect(() => {
    if (!didMount.current) {
      checkDebts([]);
    } else {
      didMount.current = true;
    }
  }, [checkDebts]);

  const columns = useColumns((params: DebtSupplierCoreItem) => {
    updateDebt(params.id, {
      finalAmount: params.finalAmount,
      id: params.id,
    });
  });

  return (
    <Box
      sx={{
        paddingLeft: 5,
        paddingRight: 5,
        width: "100%",
        "& .MuiDataGrid-columnHeader, & .MuiDataGrid-top": {
          backgroundColor: PP_BRAND_COLORS_SECONDARY,
          background: PP_BRAND_COLORS_SECONDARY,
          color: "white",
        },
        [`& .${gridClasses.checkboxInput}`]: {
          color: PP_BRAND_COLORS_PRIMARY,
        },
      }}
    >
      <DataGrid
        apiRef={apiRef}
        autoHeight
        hideFooterPagination
        rows={debts}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        onProcessRowUpdateError={(error) => {
          console.log(error);
        }}
        onRowSelectionModelChange={(rowSelectionModel) => {
          checkDebts(rowSelectionModel as DebtSupplierCoreItem["id"][]);
        }}
        isRowSelectable={(debt) => {
          return (
            debt.row.status === DebtSupplierCoreStatus.Pending &&
            isRowSelectable(+debt.row.installment, debt.row.details)
          );
        }}
        slots={{
          footer: () => (
            <div
              style={{
                backgroundColor: PP_BRAND_COLORS_SECONDARY,
                height: 50,
                borderBottomLeftRadius: 5,
                borderBottomRightRadius: 5,
                color: "white",
                fontSize: "1.25rem",
                fontWeight: "bold",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {debts?.length
                ? `Total a pagar: ${currencyFormat(
                    debts.reduce(
                      (total, debt) =>
                        debt.selected
                          ? total + (debt.finalAmount.value || 0)
                          : total,
                      0
                    ),
                    debts[0].amount.currency
                  )}`
                : ""}
            </div>
          ),
        }}
      />
    </Box>
  );
}
